/* eslint-disable prettier/prettier */
/* eslint-disable no-unused-vars */

import { I_Hotspot, I_MarketingScriptChat } from '@containers/Home/types';
import { colord, extend } from 'colord';
import { notification } from 'antd';
import namesPlugin from 'colord/plugins/names';

extend([namesPlugin]);

const PHARSE_SECRET_PANOEE = 'panoee-platform';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

export const openNotificationWithIcon = (
  type: NotificationType,
  title: string,
  description?: string,
  duration?: number,
  key?: string,
) => {
  notification[type]({
    ...(key ? { key } : {}),
    message: title,
    description,
    duration: (duration ?? 5) * 1000,
  });
};

/**
 * @param {number} input: number
 * @return number formatted, 100000 => 100,000
 */
export const formatNumber = (number: number): string => {
  const value = `${number}`;
  const list = value.split('.');
  const prefix = list[0].charAt(0) === '-' ? '-' : '';
  let num = prefix ? list[0].slice(1) : list[0];
  let result = '';
  while (num.length > 3) {
    result = `,${num.slice(-3)}${result}`;
    num = num.slice(0, num.length - 3);
  }
  if (num) {
    result = num + result;
  }
  return `${prefix}${result}${list[1] ? `.${list[1]}` : ''}`;
};

/**
 * @param func function debounced
 * @param waitFor time debounce
 */
export const debounce = <F extends (...args: any) => any>(
  func: F,
  waitFor: number,
) => {
  const timeout = 0;

  const debounced = (...args: any) => {
    clearTimeout(timeout);
    setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

export const isServer = typeof window === 'undefined';

export const toggleItemArrs = <T extends string | number>(
  arrs: T[],
  item: T,
) => {
  const listXor: T[] = arrs.some(el => el === item)
    ? arrs.filter(el => el !== item)
    : [...arrs, item];
  return listXor;
};

export const convertYoutubeUrlToEmbedUrl = url => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  const videoId = match && match[2].length === 11 ? match[2] : null;

  return `//www.youtube-nocookie.com/embed/${videoId}`;
};

export const filterHotspotVisible = (hotspot: I_Hotspot) => {
  let visible = false;
  switch (hotspot.type) {
    case 'article':
      visible = !!hotspot.config?.article?.post_id;
      break;
    case 'product':
      visible = !!hotspot.itemPrd?.id;
      break;
    case 'link':
      visible = !!hotspot.config?.link?.url;
      break;
    case 'image':
      visible = !!hotspot.config?.image?.galleries?.length;
      break;
    case 'point':
      visible = !!hotspot.config?.point?.target_scene_id;
      break;
    case 'chevron':
      visible = !!hotspot.config?.chevron?.target_scene_id;
      break;
    case 'video':
      visible = !!hotspot.config?.video?.url;
      break;
    case 'object3d':
      visible =
        !!hotspot.config?.object3d?.media?.src ||
        !!(
          hotspot.config?.object3d?.medias &&
          hotspot.config?.object3d?.medias.length > 0
        );
      break;
    case 'media':
      visible =
        (hotspot.config?.media.type !== 'media-text' &&
          !!hotspot.config?.media?.media?.src) ||
        (hotspot.config?.media.type === 'media-text' &&
          !!hotspot.config?.media?.content);
      break;
    case 'sound':
      visible =
        !!hotspot.config?.sound?.file?.src || !!hotspot.config?.sound?.url;
      break;
    case 'instructor':
      visible = !!hotspot.config?.instructor?.media?.src;
      break;
    case 'compact':
      visible = !!hotspot.config?.compact?.length;
      break;
    default:
      break;
  }
  return visible;
};

export const filterHotspotIcon = (hotspot: I_Hotspot) => {
  let image;
  switch (hotspot.type) {
    case 'point':
      if (hotspot.config.style?.type !== 'image') {
        image = `${process.env.NEXT_PUBLIC_PREFIX_NEXT_STATIC}/static/static/${hotspot.type}.png`;
      } else {
        image = hotspot.config.style.image?.src;
      }
      break;
    default:
      image = `${process.env.NEXT_PUBLIC_PREFIX_NEXT_STATIC}/static/static/${hotspot.type}.png`;
      break;
  }
  return image;
};

export const separateColorAndTransparency = (color: string) => {
  const alpha = colord(color).alpha();
  const rgba = colord(color).toRgb();
  const colorWithoutTransparency = colord(rgba).alpha(1).toHex();
  return { color: colorWithoutTransparency, alpha };
};

export const encryptText = (mess: string) => {
  const textToChars = (text: string) =>
    text.split('').map(c => c.charCodeAt(0));
  const byteHex = n => `0${Number(n).toString(16)}`.substr(-2);
  const applySaltToChar = code =>
    // eslint-disable-next-line no-bitwise
    textToChars(PHARSE_SECRET_PANOEE).reduce((a, b) => a ^ b, code);

  return mess
    .split('')
    .map(textToChars)
    .map(applySaltToChar)
    .map(byteHex)
    .join('');
};

// export const convertScriptChats = (scriptChats: I_MarketingScriptChat[]) => {
//   const htmls = scriptChats.reduce((result, chat) => result + chat.html, '');
//   const scripts = scriptChats.reduce(
//     (result, chat) => result + chat.script,
//     '',
//   );
//   return { htmls, scripts };
// };

export const convertScriptChats = (scriptChats: I_MarketingScriptChat[]) => {
  let htmls = ``;
  let scripts = '';
  scriptChats.map(chat => {
    if (chat.html == 'Facebook' && chat.script) {
      htmls += `<div id="floating-icon-facebook">
                <a href="https://m.me/${chat.script}" target="_blank">
                  <img src="${process.env.NEXT_PUBLIC_PREFIX_NEXT_STATIC}/static/static/messenger-logo.png" alt="Icon" />
                </a>
              </div>`;
    } else if (chat.html == 'Telegram' && chat.script) {
      htmls += `<div id="floating-icon-telegram">
                  <a href="https://t.me/${chat.script}" target="_blank">
                      <img src="${process.env.NEXT_PUBLIC_PREFIX_NEXT_STATIC}/static/static/telegram-logo.png" alt="Chat with us on Telegram">
                  </a>
                </div>`;
    }
  });

  return { htmls, scripts };
};
